import type { AppView, GroupBy, SavedView, ViewFilters } from "../../domain/app-types.ts";
import type { SortDirection, TaskSort } from "../../domain/entities.ts";
import { defaultSavedViewFilters } from "../navigation/saved-view-state.ts";
import { normalizePageSize, normalizeSortDirection, normalizeTaskSort } from "./selectors.ts";
import { rememberSearch } from "./search.ts";
import { unrestrictedFilters, withoutFilter } from "../navigation/active-filters.ts";

export class TaskViewState {
  view: AppView = "open";
  query = "";
  personalSavedViews: SavedView[];
  savedViews: SavedView[];
  activeSavedView: string | null = null;
  filters: ViewFilters = defaultSavedViewFilters();
  grouping: GroupBy = "none";
  sortBy: TaskSort = "manual";
  sortDirection: SortDirection = "asc";
  recentSearches: string[];

  constructor(personalSavedViews: SavedView[] = [], recentSearches: string[] = []) {
    this.personalSavedViews = personalSavedViews;
    this.savedViews = [...personalSavedViews];
    this.recentSearches = recentSearches;
  }

  replaceSavedViews(views: SavedView[]): void {
    this.savedViews = views;
  }

  updatePersonalSavedViews(): SavedView[] {
    this.personalSavedViews = this.savedViews.filter(view => !view.published);
    return this.personalSavedViews;
  }

  activateSavedView(view: SavedView): number {
    this.activeSavedView = view.id;
    this.filters = { ...defaultSavedViewFilters(), ...structuredClone(view.filters) };
    this.grouping = view.groupBy;
    this.sortBy = normalizeTaskSort(view.sortBy);
    this.sortDirection = normalizeSortDirection(view.sortDirection, view.sortBy);
    this.view = "custom";
    return normalizePageSize(view.pageSize);
  }

  clearFilters(): void {
    this.query = "";
    this.filters = defaultSavedViewFilters();
    this.grouping = "none";
    this.sortBy = "manual";
    this.sortDirection = "asc";
    this.activeSavedView = null;
    this.view = "open";
  }

  rememberSearch(): string[] {
    this.recentSearches = rememberSearch(this.recentSearches, this.query);
    return this.recentSearches;
  }

  removeVisibleFilter(key?: string): void {
    if (this.view !== "custom") return;
    const next = key ? withoutFilter(this.filters, key) : unrestrictedFilters();
    if (next === this.filters) return;
    this.filters = next;
    // This is an unsaved personal variation, never an edit to a published view.
    this.activeSavedView = null;
  }
}
